/* eslint-disable react/prop-types */
import React from "react";
import { Box, Typography, CardContent, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { formatCurrency } from "../../../utils/currencyUtils";
import { BrandPerformanceBiDto } from "../../../shared/dtos/bi/BrandPerformanceBiDto";

interface BrandPerformanceWidgetProps {
  data?: BrandPerformanceBiDto[];
}

const BrandPerformanceWidget: React.FC<BrandPerformanceWidgetProps> = ({
  data = [],
}) => {
  const theme = useTheme();
  const VIBRANT_COLORS = [
    "#00e676",
    "#00b0ff",
    "#ff9800",
    "#b388ff",
    theme.palette.secondary.main,
  ];

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Brand Performance
        </Typography>

        {data.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={data}
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={false}
                  stroke={theme.palette.neutral.dark}
                />
                <XAxis
                  type="number"
                  tickFormatter={(val) =>
                    new Intl.NumberFormat("en-US", {
                      notation: "compact",
                    }).format(val)
                  }
                  tick={{ fontSize: 11, fill: theme.palette.neutral.light }}
                />
                <YAxis
                  type="category"
                  dataKey="brandName"
                  width={100}
                  tick={{ fontSize: 11, fill: theme.palette.neutral.light }}
                  tickFormatter={(val) =>
                    val.length > 12 ? `${val.substring(0, 12)}...` : val
                  }
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 8,
                    borderColor: theme.palette.neutral.dark,
                    color: theme.palette.neutral.light,
                  }}
                  formatter={(val, _, props) => [
                    formatCurrency(Number(val)),
                    `Revenue (Profit: ${formatCurrency(props.payload.totalProfit)})`,
                  ]}
                />
                <Bar dataKey="totalRevenue" radius={[0, 4, 4, 0]} barSize={20}>
                  {data.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={VIBRANT_COLORS[index % VIBRANT_COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 300,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No brand data found.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default BrandPerformanceWidget;
